/**
 * AbilityRangeTest — ability-range-test.
 *
 * Canonical /abilitytest test surface. Single-purpose:
 *   - Player spawns at FIXED far-left position (deterministic, never
 *     random — re-spawning lands at the same X every time)
 *   - ONE pig at exactly `range + offset` from spawn (range comes from
 *     ABILITY_TEST_CONFIG.testAbilityId's TOML `range` field — fireball
 *     by default → 500px range → pig at 510px)
 *   - Distance ruler ticks every 50px, labeled with px-from-spawn (not
 *     tile index — DJ asked for distances, the load-bearing UX detail)
 *   - Walled arena, focused width (50 tiles vs PvE's 200) so the
 *     spawn-pig corridor fills the screen, not the periphery
 *
 * Why this exists: VFX iteration on fireball needs the cast↔miss
 * verification loop tight. The /pve test surface has 8 mobs at random
 * distances + the multi-dummy AbilityTestArena has 5 dummies — both
 * noisy for "is the cast getting range-gated correctly". This is the
 * focused single-NPC surface.
 *
 * Range gate (PlayerManager.performAbility STAGE 4.5 + CombatHandler
 * server gate) reads `range` from the TOML and refuses out-of-range
 * casts with feedback. At spawn DJ stands `pigOffsetFromRangePx` (10px)
 * out of range — fireball refuses with "Out of range". After walking
 * ~10px right, the cast clears.
 *
 * URL: https://h2000-ptr.djmsqrvve.com/abilitytest
 *
 * Plan: ~/.claude/plans/new-helix2000-agent-buzzing-turtle.md
 *      ("Next slice: /abilitytest + /editor permanent test surfaces")
 */

import { PlatformerLevel } from '../../shared/CharacterTypes';
import { GridLevel, LayerType } from '../../shared/TileEditorTypes';
import { ABILITY_TEST_CONFIG } from '../../config/abilitytest/AbilityTestConfig';
import { getAbilityData } from '../../shared/content/AbilityDataAdapter';

// ============================================================================
// CONSTANTS — every numeric is named, no magic numbers
// ============================================================================
const MAP_ID = 'ability-range-test';
const MAP_NAME = 'Ability Range Test';
const VERSION = '1.0';

const TILE = 32;            // tile size (px)
const W_TILES = 50;         // 1600 px wide — focused on the spawn↔pig corridor
const H_TILES = 30;         // 960 px tall

const WALL_THICKNESS_TILES = 4;
const FLOOR_TOP_ROW = 23;   // top of the floor (playable space ends here)
const FLOOR_DEPTH_TILES = 6;

const PLAYER_SPAWN_TILE_X = 5;           // 5 tiles in from the left wall (160 px)
const PLAYER_SPAWN_TILE_Y = FLOOR_TOP_ROW - 2;  // 2 tiles above floor — drops to floor

const PLAYER_SPAWN_X = PLAYER_SPAWN_TILE_X * TILE;
const PLAYER_SPAWN_Y = PLAYER_SPAWN_TILE_Y * TILE;

// Distance ruler — tick every 50 px from spawn, labeled with px distance.
const RULER_INTERVAL_PX = 50;
const RULER_FIRST_PX = 50;     // skip 0 (player sprite covers spawn)
const RULER_LAST_PX = 1100;    // covers 0..1100 from spawn = up to tile 39 of 50
const RULER_TICK_WIDTH = 4;    // 4 px wide tick line
const RULER_LABEL_INTERVAL_PX = 100;  // every other tick gets a number label

// Pig spawn — derived from the test ability's range. Reads at module
// load from the generated abilities catalog (TOML → abilities.gen.ts).
// If the ability is missing or has no `range`, fall back to a
// reasonable default so the map still boots.
const FIREBALL_RANGE_FALLBACK_PX = 500;
const testAbilityData = getAbilityData(ABILITY_TEST_CONFIG.testAbilityId);
const RESOLVED_RANGE_PX = testAbilityData?.range ?? FIREBALL_RANGE_FALLBACK_PX;
const PIG_OFFSET_FROM_SPAWN_PX = RESOLVED_RANGE_PX + ABILITY_TEST_CONFIG.pigOffsetFromRangePx;
const PIG_X = PLAYER_SPAWN_X + PIG_OFFSET_FROM_SPAWN_PX;
const PIG_Y = (FLOOR_TOP_ROW - 1) * TILE;  // pig sits 1 tile above floor — server snaps to floor surface

// ============================================================================
// PALETTE — distinct from /brawl crimson and /pve green
// ============================================================================
const COLOR_BG       = '#0a081a';   // very dark indigo — reads "calibration room"
const COLOR_FLOOR    = '#1f1a3a';   // deep purple floor
const COLOR_WALLS    = '#0e0a26';   // darker purple walls
const COLOR_TICK     = '#ffffff';   // white tick — high contrast
const COLOR_TICK_LBL = '#88c0ff';   // light blue label text — readable on dark
const COLOR_TICK_RANGE = '#ff6644'; // tick AT the range threshold gets a warning color

// ============================================================================
// PLATFORMS — walls, floor, ruler ticks
// ============================================================================
const runtimePlatforms: any[] = [
  // Floor
  {
    id: 'floor',
    x: 0,
    y: FLOOR_TOP_ROW * TILE,
    width: W_TILES * TILE,
    height: FLOOR_DEPTH_TILES * TILE,
    type: 'normal' as const,
    color: COLOR_FLOOR,
  },
  // Ceiling
  {
    id: 'ceiling',
    x: 0,
    y: 0,
    width: W_TILES * TILE,
    height: WALL_THICKNESS_TILES * TILE,
    type: 'normal' as const,
    color: COLOR_WALLS,
  },
  // Left wall
  {
    id: 'wall-left',
    x: 0,
    y: 0,
    width: WALL_THICKNESS_TILES * TILE,
    height: H_TILES * TILE,
    type: 'normal' as const,
    color: COLOR_WALLS,
  },
  // Right wall
  {
    id: 'wall-right',
    x: (W_TILES - WALL_THICKNESS_TILES) * TILE,
    y: 0,
    width: WALL_THICKNESS_TILES * TILE,
    height: H_TILES * TILE,
    type: 'normal' as const,
    color: COLOR_WALLS,
  },
];

// Distance ruler — every 50 px from spawn, labeled with px distance.
// Tick at the range threshold is highlighted (warning color) so DJ
// can see at a glance where "out of range" begins.
for (let dist = RULER_FIRST_PX; dist <= RULER_LAST_PX; dist += RULER_INTERVAL_PX) {
  const isAtRange = dist === RESOLVED_RANGE_PX;
  const isLabeled = dist % RULER_LABEL_INTERVAL_PX === 0 || isAtRange;
  runtimePlatforms.push({
    id: `ruler-${dist}`,
    x: PLAYER_SPAWN_X + dist - RULER_TICK_WIDTH / 2,
    y: (FLOOR_TOP_ROW - 1) * TILE,  // 1 tile above floor top
    width: RULER_TICK_WIDTH,
    height: TILE,
    type: 'decorative' as const,
    color: isAtRange ? COLOR_TICK_RANGE : COLOR_TICK,
    label: isLabeled ? String(dist) : undefined,
    labelColor: isAtRange ? COLOR_TICK_RANGE : COLOR_TICK_LBL,
  });
}

// ============================================================================
// LAVA STRIP (L2.2 zone POC) — far-right hazard, well past the pig so
// it doesn't interfere with the spawn↔pig range-test corridor. DJ has
// to deliberately walk into it to verify zone damage ticks.
// ============================================================================
const LAVA_ZONE_ID = 'lava_strip_far_right';
const LAVA_OFFSET_FROM_SPAWN_PX = 1000;             // beyond all ruler ticks
const LAVA_X = PLAYER_SPAWN_X + LAVA_OFFSET_FROM_SPAWN_PX;
const LAVA_WIDTH_TILES = 6;
const LAVA_WIDTH_PX = LAVA_WIDTH_TILES * TILE;      // 192 px wide
const LAVA_TOP_Y = (FLOOR_TOP_ROW - 1) * TILE;      // 1 tile above floor surface
const LAVA_HEIGHT_PX = TILE + 4;                    // ~36 px tall — visible strip
const LAVA_DAMAGE_PER_TICK = 5;
const LAVA_TICK_RATE_MS = 1000;
const COLOR_LAVA = '#ff4422';

// Decorative platform: gives the lava a visible orange strip.
runtimePlatforms.push({
  id: LAVA_ZONE_ID,
  x: LAVA_X,
  y: LAVA_TOP_Y,
  width: LAVA_WIDTH_PX,
  height: LAVA_HEIGHT_PX,
  type: 'decorative' as const,
  color: COLOR_LAVA,
  label: 'LAVA',
  labelColor: '#ffeecc',
});

// ============================================================================
// PARALLAX — minimal, lets the ruler ticks dominate
// ============================================================================

// ============================================================================
// EXPORT
// ============================================================================
export const AbilityRangeTest: GridLevel & Partial<PlatformerLevel> = {
  id: MAP_ID,
  name: MAP_NAME,
  version: VERSION,

  widthTiles: W_TILES,
  heightTiles: H_TILES,
  width: W_TILES * TILE,
  height: H_TILES * TILE,
  tileSize: TILE,

  backgroundColor: COLOR_BG,
  gravity: 1000,

  // Single fixed spawn point + matching defaultSpawn = deterministic
  // far-left spawn on every join AND every respawn. No random pick.
  spawnPoints: [{ x: PLAYER_SPAWN_X, y: PLAYER_SPAWN_Y }],
  defaultSpawn: { x: PLAYER_SPAWN_X, y: PLAYER_SPAWN_Y },


  layers: [
    { name: LayerType.GROUND,    visible: true, locked: false, opacity: 1.0, tiles: new Map() },
    { name: LayerType.COLLISION, visible: true, locked: false, opacity: 1.0, tiles: new Map() },
    { name: LayerType.OBJECTS,   visible: true, locked: false, opacity: 1.0, tiles: new Map() },
    { name: LayerType.ENTITIES,  visible: true, locked: false, opacity: 1.0, tiles: new Map(), entities: [] },
    { name: LayerType.TRIGGERS,  visible: true, locked: false, opacity: 1.0, tiles: new Map() },
  ],

  platforms: runtimePlatforms,

  // Pig spawned via level.entities[] — server reads at room init
  // (EntitySpawnerService.spawnMapEntities) and snaps Y to the floor
  // surface. NOT via content/zones/<id>.toml because the pig's X
  // depends on a runtime-computed value (ability range from
  // abilities.gen.ts), and TOML is build-time-frozen content.
  entities: [
    {
      id: `${MAP_ID}_pig_target`,
      type: ABILITY_TEST_CONFIG.mobId,
      x: PIG_X,
      y: PIG_Y,
      properties: {
        displayName: `Target Pig (${RESOLVED_RANGE_PX}+${ABILITY_TEST_CONFIG.pigOffsetFromRangePx}px)`,
      },
    },
  ],
  spawnRules: [],
  // 'town' (persistent / deterministic) — combined with the
  // single-element spawnPoints array + defaultSpawn, every respawn
  // lands at the same far-left position. 'field' would randomize.
  type: 'town',

  metadata: {
    author: 'DJ',
    description: `Ability range-test surface. ${ABILITY_TEST_CONFIG.testAbilityId} (range=${RESOLVED_RANGE_PX}px) vs single pig at ${PIG_OFFSET_FROM_SPAWN_PX}px. Cast refuses at spawn, succeeds after small step right.`,
    tags: ['testing', 'abilities', 'range-gate', 'vfx-iteration', 'zones-poc'],
    // Lobby tools available so DJ can /spawnmob extras while testing.
    lobbyTools: { enabled: true },
    // PvE-default profile — PvP off, friendlyFire off, ignore bots.
    // No overrides needed; the map IS a PvE single-target test.
    profile: 'pve_default',
    // L2.2 — lava strip POC. ZoneSystem (server) reads this on level
    // load, runs per-tick AABB containment, applies a `lava_damage`
    // state to players inside that ticks `LAVA_DAMAGE_PER_TICK` damage
    // every `LAVA_TICK_RATE_MS`. Death routes through L1's
    // handlePlayerDefeated — full ghost window + spawn rotation +
    // penalty pipeline engages naturally.
    zones: [
      {
        id: LAVA_ZONE_ID,
        type: 'lava',
        x: LAVA_X,
        y: LAVA_TOP_Y,
        width: LAVA_WIDTH_PX,
        height: LAVA_HEIGHT_PX,
        amount: LAVA_DAMAGE_PER_TICK,
        tickRateMs: LAVA_TICK_RATE_MS,
        label: 'lava strip',
      },
    ],
  },
};

// Re-export the resolved values so tests / diagnostics can read them
// without re-deriving (and so the IIFE-style runtime computation is
// visible from outside this module).
export {
  W_TILES,
  H_TILES,
  TILE,
  MAP_ID,
  MAP_NAME,
  PLAYER_SPAWN_X,
  PLAYER_SPAWN_Y,
  RESOLVED_RANGE_PX,
  PIG_X,
  PIG_Y,
};
